import React, { useState, useEffect } from 'react';
import '../dashboard/Dashboard.css'; 
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faCopy } from '@fortawesome/free-solid-svg-icons';
import { encrypt } from '../utils/crypt';

// Styled Components
const StyledTable = styled.table`
  min-width: 2000px;
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  table-layout: fixed;

  th, td {
    padding: 10px 8px;
    font-size: 0.85rem;
    border: 1px solid #1a1a1a;
    text-align: center;
    vertical-align: middle;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #e0e0e0;
  }

  th {
    background-color: #0d0d0d;
    color: #999999;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.05em;
  }

  tr:nth-child(even) {
    background-color: #0a0a0a;
  }

  tr:hover {
    background-color: rgba(0, 214, 122, 0.05);
  }

  th:nth-child(1), td:nth-child(1) {
    width: 10%; // Title column
  }

  th:nth-child(2), td:nth-child(2) {
    width: 15%; // Domain column
  }

  th:nth-child(3), td:nth-child(3) {
    width: 8%; // Path column
  }

  th:nth-child(4), td:nth-child(4) {
    width: 8%; // Vault column
  }

  th:nth-child(5), td:nth-child(5) {
    width: 12%; // Description column
  }

  th:nth-child(6), td:nth-child(6) {
    width: 8%; // Type column
  }

  th:nth-child(7), td:nth-child(7) {
    width: 8%; // Flow column
  }

  th:nth-child(8), td:nth-child(8) {
    width: 8%; // Model column
  }

  th:nth-child(9), td:nth-child(9) {
    width: 5%; // Public column
  }

  th:nth-child(10), td:nth-child(10) {
    width: 8%; // Users column
  }

  th:nth-child(11), td:nth-child(11) {
    width: 10%; // Deployment ID column
  }

  th:nth-child(12), td:nth-child(12) {
    width: 10%; // Encrypted Email column
  }

  th:nth-child(13), td:nth-child(13) {
    width: 8%; // Edit column
  }

  @media (max-width: 768px) {
    min-width: 2000px; // Maintain minimum width on mobile
    
    th, td {
      padding: 5px;
      font-size: 0.8rem;
    }
  }
`;


const StyledTableContainer = styled.div`
    padding: 20px;
    overflow-x: auto; // Enable horizontal scrolling
    overflow-y: visible;

    @media (max-width: 768px) {
    padding: 10px; // Reduced padding for mobile view
    }
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PaginationButton = styled.button`
  margin-right: 10px;

  @media (max-width: 768px) {
    font-size: 12px; // Smaller text on mobile
    padding: 5px; // Smaller padding on mobile
    margin-right: 5px; // Less margin on mobile
  }
`;

const PageIndicator = styled.span`
  margin-right: 10px;

  @media (max-width: 768px) {
    font-size: 14px; // Smaller text on mobile
  }
`;

const JumpToInput = styled.input`
  margin-left: 10px;

  @media (max-width: 768px) {
    font-size: 12px; // Smaller text on mobile
    padding: 5px; // Smaller padding on mobile
  }
`;

const PaginationSection = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    font-size: 14px; // Smaller text on mobile
    margin-top: 10px; // Add space between sections on mobile
  }
`;

const CopyContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const CopyButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${props => props.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const CopyableText = styled.span`
  max-width: calc(100% - 30px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function escapeBadChars(encryptedStr) {
  if (!encryptedStr) return encryptedStr;
  
  // Escape ALL non-alphanumeric characters except safe ones
  // Only allow: letters (a-z, A-Z), numbers (0-9), and safe chars (. _ -)
  return encryptedStr.replace(/[^a-zA-Z0-9._-]/g, match => {
      return `&#${match.charCodeAt(0)};`;
  });
}

const transformData = (incomingData) => {
  if (!incomingData) return [];

  return Object.keys(incomingData).map(key => {
    try {
      // First attempt: Process using the first structure
      return {
        title: incomingData[key].TITLE,
        domain: incomingData[key].DOMAIN,
        forwardSlash: incomingData[key].FORWARDSLASH,
        vault: incomingData[key].VAULT,
        description: incomingData[key].DESCRIPTION || '-',
        chat: incomingData[key].CHAT,
        data: incomingData[key].DATA,
        conv: incomingData[key].CONV,
        convFlow: incomingData[key].CONV_FLOW,
        edit: incomingData[key].EDIT,
        del: incomingData[key].DELETE,
        model: incomingData[key].MODEL,
        dark: incomingData[key].DARK,
        JSN: incomingData[key].JSON,
        context: incomingData[key].CONTEXT,
        // Add these two new fields
        isPublic: incomingData[key].IS_PUBLIC !== undefined ? incomingData[key].IS_PUBLIC : true,
        users: incomingData[key].AUTH_USERS || [],
        // Add deployment ID (use the key as deployment ID)
        deploymentId: key
      };
    } catch (error) {
      // Fallback: Process using the second structure
      return {
        title: incomingData[key].title || incomingData[key].TITLE,
        domain: incomingData[key].domain || incomingData[key].DOMAIN,
        forwardSlash: incomingData[key].forwardSlash || incomingData[key].FORWARDSLASH,
        vault: incomingData[key].vault || incomingData[key].VAULT,
        description: incomingData[key].description || incomingData[key].DESCRIPTION || '-',
        chat: incomingData[key].chat || incomingData[key].CHAT,
        data: incomingData[key].data || incomingData[key].DATA,
        conv: incomingData[key].conv || incomingData[key].CONV,
        convFlow: incomingData[key].convFlow || incomingData[key].CONV_FLOW,
        edit: incomingData[key].edit || incomingData[key].EDIT,
        del: incomingData[key].del || incomingData[key].DELETE,
        model: incomingData[key].model || incomingData[key].MODEL || incomingData[key].gpt4,
        dark: incomingData[key].dark || incomingData[key].DARK,
        JSN: incomingData[key].JSN || incomingData[key].JSON,
        context: incomingData[key].context || incomingData[key].CONTEXT,
        // Add these two new fields
        isPublic: incomingData[key].isPublic !== undefined ? incomingData[key].isPublic : 
                  incomingData[key].IS_PUBLIC !== undefined ? incomingData[key].IS_PUBLIC : true,
        users: incomingData[key].users || incomingData[key].AUTH_USERS || [],
        // Add deployment ID (use the key as deployment ID)
        deploymentId: key
      };
    }
  });
};

function formatTimestamp(timestamp) {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  const options = { year: '2-digit', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false };
  return date.toLocaleString('en-US', options);
}

function formatUnixTimestamp(unixTimestamp) {
  const date = new Date(unixTimestamp * 1000); // Convert to milliseconds
  const options = { year: '2-digit', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false };
  return date.toLocaleString('en-US', options);
}

function getDeploymentType(item) {
  const types = [];
  if (item.chat) types.push('Chat');
  if (item.data) types.push('Data');
  if (item.conv) types.push('Conversation');
  
  if (types.length === 0) return '-';
  return types.join(', ');
}

// Main Component
function Deployments({ isDarkMode, styles, depsBrah, onEdit, onDelete, userEmail }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [databaseData, setDatabaseData] = useState(false);
  const [displayedData, setDisplayedData] = useState([]); // Added displayedData state
  const [totalItems, setTotalItems] = useState(0);
  const [desiredPage, setDesiredPage] = useState(1);

  // Generate encrypted email
  const encryptedEmail = userEmail ? escapeBadChars(encrypt(userEmail)) : '';

  // Copy to clipboard function
  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      // Optional: Add a temporary notification or toast
      console.log(`${type} copied to clipboard:`, text);
    } catch (err) {
      console.error('Failed to copy: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        console.log(`${type} copied to clipboard (fallback):`, text);
      } catch (fallbackErr) {
        console.error('Fallback copy failed: ', fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handlePage = (newPage) => {
      setCurrentPage(newPage);
  };

  // useEffect for pagination
  useEffect(() => {
    const transformedData = transformData(depsBrah);
    setDatabaseData(transformedData);
    setTotalItems(transformedData.length);
  }, [depsBrah]); 

    // useEffect for pagination
    useEffect(() => {
      if (databaseData.length > 0) {
          const start = (currentPage - 1) * itemsPerPage;
          const end = start + itemsPerPage;
          setDisplayedData(databaseData.slice(start, end));
      }
  }, [currentPage, itemsPerPage, databaseData]); // Depend on currentPage, itemsPerPage, and databaseData


  return (
      <div style={styles.wrapper}>
        <div className="App" style={{...styles.body, paddingTop: "50px"}}>
        <StyledTableContainer>
                <h1 style={{marginTop: "100px"}}>
                    Deployments: <span> {databaseData.length} </span>
                </h1>
                <div style={{ overflowX: 'auto', overflowY: 'visible', width: '100%' }}>
                  <StyledTable isDarkMode={isDarkMode}>
                    <thead>
                        <tr>
                            <th>Title</th> 
                            <th>Domain</th> 
                            <th>Path</th> 
                            <th>Vault</th> 
                            <th>Description</th>
                            <th>Type</th>
                            <th>Flow</th>
                            <th>Model</th>
                            <th>Public</th>
                            <th>Users</th>
                            <th>Deployment ID</th>
                            <th>Encrypted Email</th>
                            <th>Actions</th> 
                        </tr>
                    </thead>
                    <tbody>
                        {displayedData.map((item, index) => ( // Use displayedData instead of databaseData
                            <tr key={index}>
                              <td title={item.title}>{item.title}</td>
                              <td>
                                <a href={item.domain} target="_blank" rel="noopener noreferrer" title={item.domain}>
                                  {item.domain}
                                </a>
                              </td>
                              <td title={item.forwardSlash}>{item.forwardSlash}</td>
                              <td title={item.vault}>{item.vault}</td>
                              <td title={item.description}>{item.description || '-'}</td>
                              <td>{getDeploymentType(item)}</td>
                              <td>{item.conv && item.convFlow ? item.convFlow : '-'}</td>
                              <td title={item.model}>{item.model || '-'}</td>
                              <td>{item.isPublic ? 'Yes' : 'No'}</td>
                              <td title={item.users?.length > 0 ? item.users.map(u => u.username).join(', ') : 'None'}>
                                {item.users?.length > 0 ? `${item.users.length} user(s)` : 'None'}
                              </td>
                              <td>
                                <CopyContainer>
                                  <CopyableText title={item.deploymentId}>
                                    {item.deploymentId}
                                  </CopyableText>
                                  <CopyButton 
                                    onClick={() => copyToClipboard(item.deploymentId, 'Deployment ID')}
                                    title="Copy Deployment ID"
                                    isDarkMode={isDarkMode}
                                  >
                                    <FontAwesomeIcon 
                                      icon={faCopy} 
                                      style={{ 
                                        fontSize: '12px', 
                                        color: isDarkMode ? '#999999' : '#6b7280' 
                                      }} 
                                    />
                                  </CopyButton>
                                </CopyContainer>
                              </td>
                              <td>
                                <CopyContainer>
                                  <CopyableText title={encryptedEmail}>
                                    {encryptedEmail.length > 15 ? encryptedEmail.substring(0, 15) + '...' : encryptedEmail}
                                  </CopyableText>
                                  <CopyButton 
                                    onClick={() => copyToClipboard(encryptedEmail, 'Encrypted Email')}
                                    title="Copy Encrypted Email"
                                    isDarkMode={isDarkMode}
                                  >
                                    <FontAwesomeIcon 
                                      icon={faCopy} 
                                      style={{ 
                                        fontSize: '12px', 
                                        color: isDarkMode ? '#999999' : '#6b7280' 
                                      }} 
                                    />
                                  </CopyButton>
                                </CopyContainer>
                              </td>
                              <td>
                                <button onClick={() => onEdit(item)} style={{ border: 'none', background: 'none', marginRight: '10px' }}>
                                  <FontAwesomeIcon icon={faEdit} style={{ fontSize: '15px', color: isDarkMode ? '#e0e0e0' : '#374151' }} />
                                </button>
                                <button onClick={() => onDelete(item.title)} style={{ border: 'none', background: 'none' }}>
                                  <FontAwesomeIcon icon={faTrash} style={{ fontSize: '15px', color: isDarkMode ? '#e0e0e0' : '#374151' }} />
                                </button>
                              </td>
                            </tr>
                        ))}
                    </tbody>
                  </StyledTable>
                </div>
            {/* Pagination controls */}
            <PaginationContainer>
            <div>
                <PaginationButton 
                className={ isDarkMode ? 'btn-dark' : 'btn-light' }
                style={{ marginLeft: "0px" }}
                onClick={() => handlePage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                >
                Previous
                </PaginationButton>
                <PageIndicator>
                Page {currentPage} of {Math.ceil(totalItems / itemsPerPage)}
                </PageIndicator>
                <PaginationButton 
                className={ isDarkMode ? 'btn-dark' : 'btn-light' }
                style={{ marginLeft: "0px" }}
                onClick={() => handlePage(Math.min(currentPage + 1, Math.ceil(totalItems / itemsPerPage)))}
                disabled={currentPage === Math.ceil(totalItems / itemsPerPage)}
                >
                Next
                </PaginationButton>
            </div>
            <PaginationSection>
                <a style={{ marginTop: '0px' }}>Page: </a>
                <JumpToInput 
                className={ isDarkMode ? 'sleek-input-dark' : 'sleek-input-light' }
                style={{ marginTop:"-0px", maxWidth: "75px" }} 
                type="number" 
                value={desiredPage}
                onChange={e => setDesiredPage(Number(e.target.value))}
                />
                <PaginationButton 
                className={ isDarkMode ? 'btn-dark' : 'btn-light' }
                style={{ marginTop:"2px", marginRight: "0px" }} 
                onClick={() => setCurrentPage(Math.max(1, Math.min(desiredPage, Math.ceil(totalItems / itemsPerPage))))}
                >
                Go
                </PaginationButton>
            </PaginationSection>
            </PaginationContainer>
    
            {/* Bottom of Page Spacing */}
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
        </StyledTableContainer>
      </div>
    </div>
  );
}


export default Deployments;
